// PIPE NETWORK DRAWER v5 — All fixes applied
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useCallback } from "react";
import { C } from "../../../constants";

function iso(x, y, z, r) {
  var a = (r || 0) * Math.PI / 180;
  var rx = x * Math.cos(a) - y * Math.sin(a);
  var ry = x * Math.sin(a) + y * Math.cos(a);
  return { sx: (rx - ry) * 0.866, sy: (rx + ry) * 0.5 - z };
}

var SC = 70, MNZ = 0.2, MXZ = 4;
var DIAMS = ["15", "20", "25", "32", "40", "50", "65", "80"];
var PTYPES = ["40T", "80T"];
var DIRS = {
  N: { dx: 0, dy: -1, dz: 0 }, S: { dx: 0, dy: 1, dz: 0 },
  E: { dx: 1, dy: 0, dz: 0 }, W: { dx: -1, dy: 0, dz: 0 },
  NE: { dx: .707, dy: -.707, dz: 0 }, NW: { dx: -.707, dy: -.707, dz: 0 },
  SE: { dx: .707, dy: .707, dz: 0 }, SW: { dx: -.707, dy: .707, dz: 0 },
  UP: { dx: 0, dy: 0, dz: 1 }, DOWN: { dx: 0, dy: 0, dz: -1 },
};
var NC = { cylinder: "#DC2626", junction: "#059669", nozzle: "#2563EB", tee: "#D97706", hose: "#3B82F6" };

function gid() { return Math.random().toString(36).substr(2, 6); }
function nById(n, id) { return n.nodes.find(function(x) { return x.id === id; }); }
function eFor(n, id) { return n.edges.filter(function(e) { return e.from === id || e.to === id; }); }

function nSum(n) {
  var p = {}, t = 0, nz = 0;
  n.edges.forEach(function(e) {
    if (e.edgeType === "hose") return;
    var k = e.diameter + "mm";
    if (!p[k]) p[k] = { length: 0, count: 0 };
    p[k].length += parseFloat(e.length) || 0;
    p[k].count++;
  });
  n.nodes.forEach(function(nd) {
    if (nd.type === "nozzle") nz++;
    if (nd.type === "tee") t++;
  });
  return { pipes: p, tees: t, nozzles: nz, totalNodes: n.nodes.length, totalEdges: n.edges.filter(function(e) { return e.edgeType !== "hose"; }).length };
}

// Initial network: CYL node is where valve is, hose exits W from valve
function initNet() {
  return {
    nodes: [
      { id: "cyl", type: "cylinder", x: 0, y: 0, z: 0, label: "CYL" },
      { id: "hose_end", type: "junction", x: -0.5 * SC, y: 0, z: 0, label: "1" }
    ],
    edges: [
      { id: "e_hose", from: "cyl", to: "hose_end", length: "0.50", diameter: "25", pipeType: "", direction: "W", edgeType: "hose" }
    ],
    nextLabel: 2
  };
}

function isoBounds(net, rot, pad) {
  pad = pad || 120;
  if (net.nodes.length === 0) return { x: -300, y: -300, w: 600, h: 600 };
  var a = Infinity, b = Infinity, c = -Infinity, d = -Infinity;
  net.nodes.forEach(function(n) {
    var q = iso(n.x, n.y, n.z || 0, rot);
    if (q.sx < a) a = q.sx; if (q.sy < b) b = q.sy;
    if (q.sx > c) c = q.sx; if (q.sy > d) d = q.sy;
  });
  // Extra padding below cylinder for body
  return { x: a - pad, y: b - pad, w: Math.max(c - a + pad * 2, 400), h: Math.max(d - b + pad * 2 + 60, 400) };
}

// ─── Compass Rose ───
function Compass({ onSelect, disabled, rot }) {
  var sz = 180, cx = sz / 2, cy = sz / 2 + 16, r = sz * .3, br = 16;
  // Positions match isometric projection, rotate with view
  var basePos = [
    { ax: r * .707, ay: -r * .707 },   // N in iso
    { ax: r, ay: 0 },                   // NE
    { ax: r * .707, ay: r * .707 },     // E
    { ax: 0, ay: r },                   // SE
    { ax: -r * .707, ay: r * .707 },    // S
    { ax: -r, ay: 0 },                  // SW
    { ax: -r * .707, ay: -r * .707 },   // W
    { ax: 0, ay: -r },                  // NW
  ];
  var dirLabels = ["N","NE","E","SE","S","SW","W","NW"];
  var rotShift = Math.round(((rot||0) / 45) % 8);
  if (rotShift < 0) rotShift += 8;
  var dirs = dirLabels.map(function(k, i) {
    var pi = (i + rotShift) % 8;
    return { k: k, ax: basePos[pi].ax, ay: basePos[pi].ay };
  });
  var bf = disabled ? C.grayBg : C.white, bs = disabled ? C.border : C.navy, tf = disabled ? C.t3 : C.navy;
  return (
    <svg width={sz} height={sz + 50} style={{ display: "block", margin: "0 auto" }}>
      <g style={{ cursor: disabled ? "not-allowed" : "pointer" }} onClick={function() { if (!disabled) onSelect("UP"); }}>
        <rect x={cx - 28} y={0} width={56} height={24} rx={6} fill={bf} stroke={bs} strokeWidth="1.5" />
        <text x={cx} y={16} textAnchor="middle" fontSize="11" fontWeight="700" fill={tf} style={{ userSelect: "none" }}>▲ UP</text>
      </g>
      <text x={cx} y={32} textAnchor="middle" fontSize="8" fill={C.t3} style={{ userSelect: "none" }}>Elevation Up</text>
      <circle cx={cx} cy={cy} r={r + 18} fill="none" stroke={C.border} strokeWidth="1" strokeDasharray="4 2" />
      <circle cx={cx} cy={cy} r={3} fill={C.navy} />
      {dirs.map(function(d) {
        return (
          <g key={d.k} style={{ cursor: disabled ? "not-allowed" : "pointer" }} onClick={function() { if (!disabled) onSelect(d.k); }}>
            <line x1={cx} y1={cy} x2={cx + d.ax} y2={cy + d.ay} stroke={C.border} strokeWidth="1" />
            <circle cx={cx + d.ax} cy={cy + d.ay} r={br} fill={bf} stroke={bs} strokeWidth="1.5" />
            <text x={cx + d.ax} y={cy + d.ay + 4} textAnchor="middle" fontSize="9" fontWeight="700" fill={tf} style={{ userSelect: "none" }}>{d.k}</text>
          </g>
        );
      })}
      <g style={{ cursor: disabled ? "not-allowed" : "pointer" }} onClick={function() { if (!disabled) onSelect("DOWN"); }}>
        <rect x={cx - 28} y={cy + r + 26} width={56} height={24} rx={6} fill={bf} stroke={bs} strokeWidth="1.5" />
        <text x={cx} y={cy + r + 42} textAnchor="middle" fontSize="11" fontWeight="700" fill={tf} style={{ userSelect: "none" }}>▼ DOWN</text>
      </g>
      <text x={cx} y={cy + r + 58} textAnchor="middle" fontSize="8" fill={C.t3} style={{ userSelect: "none" }}>Elevation Down</text>
    </svg>
  );
}

// ─── SVG Icons ───
// Cylinder: valve at node position, body hangs BELOW. Hose connects to node pos (= valve)
function CylSVG({ sx, sy, sel, cylSize }) {
  return (
    <g transform={"translate(" + sx + "," + sy + ")"}>
      {/* Valve at node position (0,0) — red to match cylinder */}
      <rect x={-5} y={-4} width={10} height={8} rx={1.5} fill={sel ? "#FEF3C7" : "#FCA5A5"} stroke={NC.cylinder} strokeWidth={sel ? 2 : 1.2} />
      {/* Cylinder body below valve */}
      <ellipse cx={0} cy={8} rx={12} ry={4} fill="#FCA5A5" stroke={NC.cylinder} strokeWidth={1.5} />
      <rect x={-12} y={8} width={24} height={28} rx={3} fill={sel ? "#FEF3C7" : "#FEE2E2"} stroke={NC.cylinder} strokeWidth={sel ? 2.5 : 1.5} />
      <ellipse cx={0} cy={36} rx={12} ry={4} fill={sel ? "#FEF3C7" : "#FEE2E2"} stroke={NC.cylinder} strokeWidth={1.5} />
      <text y={48} textAnchor="middle" fontSize="8" fontWeight="700" fill={NC.cylinder} style={{ userSelect: "none" }}>CYL</text>
      {cylSize && <text y={57} textAnchor="middle" fontSize="7" fill={C.t3} style={{ userSelect: "none" }}>{cylSize}</text>}
    </g>
  );
}

function NozSVG({ sx, sy, sel, nType, label }) {
  return (
    <g transform={"translate(" + sx + "," + sy + ")"}>
      {nType === "360°" ? (
        <><circle r={10} fill={sel ? "#DBEAFE" : "#EFF6FF"} stroke={NC.nozzle} strokeWidth={sel ? 2.5 : 1.5} /><circle r={3} fill={NC.nozzle} /></>
      ) : (
        <polygon points="-10,8 10,8 0,-10" fill={sel ? "#DBEAFE" : "#EFF6FF"} stroke={NC.nozzle} strokeWidth={sel ? 2.5 : 1.5} />
      )}
      <text y={22} textAnchor="middle" fontSize="8" fontWeight="600" fill={NC.nozzle} style={{ userSelect: "none" }}>{label}</text>
    </g>
  );
}

function JncSVG({ sx, sy, sel, label, type }) {
  return (
    <g transform={"translate(" + sx + "," + sy + ")"}>
      {type === "tee" ? (
        <rect x={-6} y={-6} width={12} height={12} rx={2} fill={sel ? "#FEF3C7" : "#FEF9C3"} stroke={NC.tee} strokeWidth={sel ? 2.5 : 1.5} />
      ) : (
        <circle r={5} fill={sel ? "#D1FAE5" : "#ECFDF5"} stroke={NC.junction} strokeWidth={sel ? 2.5 : 1.5} />
      )}
      <text y={-10} textAnchor="middle" fontSize="8" fontWeight="600" fill={C.t2} style={{ userSelect: "none" }}>{label}</text>
    </g>
  );
}

// ─── Orientation lines through cylinder ───
function OrientationLines({ cx, cy, rot }) {
  var len = 60;
  var n = iso(0, -len, 0, rot), s = iso(0, len, 0, rot);
  var e = iso(len, 0, 0, rot), w = iso(-len, 0, 0, rot);
  return (
    <g opacity="0.25">
      <line x1={cx + n.sx} y1={cy + n.sy} x2={cx + s.sx} y2={cy + s.sy} stroke="#94A3B8" strokeWidth="0.5" strokeDasharray="3 2" />
      <line x1={cx + e.sx} y1={cy + e.sy} x2={cx + w.sx} y2={cy + w.sy} stroke="#94A3B8" strokeWidth="0.5" strokeDasharray="3 2" />
      <text x={cx + n.sx * 1.15} y={cy + n.sy * 1.15} textAnchor="middle" fontSize="7" fill="#94A3B8">N</text>
      <text x={cx + s.sx * 1.15} y={cy + s.sy * 1.15} textAnchor="middle" fontSize="7" fill="#94A3B8">S</text>
      <text x={cx + e.sx * 1.15} y={cy + e.sy * 1.15} textAnchor="middle" fontSize="7" fill="#94A3B8">E</text>
      <text x={cx + w.sx * 1.15} y={cy + w.sy * 1.15} textAnchor="middle" fontSize="7" fill="#94A3B8">W</text>
    </g>
  );
}

// ─── Properties Panel ───
function PropPanel({ network, selectedId, onUpdateEdge, onAddPipe, onAddNozzle, onRemoveNode, onRemoveEdge, isMetric, nozzleType, suggestedNozzles, cylSize, valveMM,rot }) {
  var uL = isMetric ? "m" : "ft";
  var sn = network.nodes.find(function(n) { return n.id === selectedId; });
  var se = network.edges.find(function(e) { return e.id === selectedId; });
  var _pl = useState("1.00"), pl = _pl[0], spl = _pl[1];
  var _pd = useState(valveMM || "25"), pd = _pd[0], spd = _pd[1];
  var _pt = useState("40T"), pt = _pt[0], spt = _pt[1];
  var ls = { fontSize: 11, fontWeight: 600, color: C.t2, marginBottom: 4, display: "block" };
  var ist = { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid " + C.border, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", background: C.white, color: C.t1 };
  var sst = Object.assign({}, ist, { appearance: "auto" });
  var curNoz = nSum(network).nozzles;

  if (sn) {
    var edg = eFor(network, sn.id), isTerm = edg.length <= 1;
    var canPipe = sn.type !== "nozzle" && sn.type !== "cylinder";
    var canNoz = isTerm && sn.type !== "cylinder" && sn.type !== "nozzle";
    var canRm = sn.type !== "cylinder" && sn.id !== "hose_end" && isTerm;
    return (
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: NC[sn.type] || C.navy }} />
          Node {sn.label}
          <span style={{ fontSize: 11, fontWeight: 400, color: C.t3, textTransform: "capitalize" }}>({sn.type})</span>
        </div>
        <div style={{ fontSize: 12, color: C.t3, marginBottom: 16 }}>
          Connections: {edg.length}{(sn.z || 0) !== 0 ? " | Elev: " + ((sn.z || 0) / SC).toFixed(2) + uL : ""}
        </div>
        {suggestedNozzles > 0 && (
          <div style={{ padding: "8px 12px", borderRadius: 8, background: "#EFF6FF", border: "1px solid #BFDBFE", marginBottom: 12, fontSize: 11, color: "#1E40AF" }}>
            <strong>Suggested nozzles:</strong> {suggestedNozzles} (placed: {curNoz})
            {curNoz !== suggestedNozzles && <span style={{ color: "#B45309", display: "block", marginTop: 2 }}>⚠ Nozzle count differs from calculated suggestion</span>}
          </div>
        )}
        {canNoz && (
          <button onClick={function() { onAddNozzle(sn.id); }}
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1.5px solid " + NC.nozzle, background: NC.nozzle + "08", color: NC.nozzle, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", marginBottom: 12 }}>
            + Add Nozzle Here
          </button>
        )}
        {canPipe && (
          <div style={{ background: C.grayBg, borderRadius: 10, padding: 14, border: "1px solid " + C.border }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 12 }}>Add Pipe From Node {sn.label}</div>
            <div style={{ marginBottom: 10 }}>
              <span style={ls}>Length ({uL})</span>
              <input type="number" step="0.01" min="0.01" value={pl} onChange={function(e) { spl(e.target.value); }} style={ist} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <div><span style={ls}>Diameter (mm)</span><select value={pd} onChange={function(e) { spd(e.target.value); }} style={sst}>{DIAMS.map(function(d) { return <option key={d} value={d}>{d} mm</option>; })}</select></div>
              <div><span style={ls}>Type</span><select value={pt} onChange={function(e) { spt(e.target.value); }} style={sst}>{PTYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}</select></div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.t2, marginBottom: 6 }}>Select Direction</div>
            <Compass onSelect={function(dir) { onAddPipe(sn.id, dir, parseFloat(pl) || 1, pd, pt); }} disabled={false} rot={rot} />
          </div>
        )}
        {canRm && (
          <button onClick={function() { onRemoveNode(sn.id); }}
            style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: C.red, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", marginTop: 12 }}>
            Remove Node
          </button>
        )}
      </div>
    );
  }

  if (se) {
    var isHose = se.edgeType === "hose";
    return (
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 12 }}>{isHose ? "Flex Discharge Hose" : "Pipe Segment"}</div>
        <div style={{ fontSize: 12, color: C.t3, marginBottom: 16 }}>
          Node {(nById(network, se.from) || {}).label} → Node {(nById(network, se.to) || {}).label}
          {se.direction ? " | " + se.direction : ""}
        </div>
        {isHose ? (
          <div style={{ padding: 8, borderRadius: 8, background: "#FEF3C7", border: "1px solid #FDE68A", fontSize: 11, color: "#92400E", marginBottom: 12 }}>
            Flexible metal braided discharge hose — factory standard. Length is fixed.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 10 }}>
              <span style={ls}>Length ({uL})</span>
              <input type="number" step="0.01" min="0.01" value={se.length} onChange={function(e) { onUpdateEdge(se.id, "length", e.target.value); }} style={ist} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
              <div><span style={ls}>Diameter (mm)</span><select value={se.diameter} onChange={function(e) { onUpdateEdge(se.id, "diameter", e.target.value); }} style={sst}>{DIAMS.map(function(d) { return <option key={d} value={d}>{d} mm</option>; })}</select></div>
              <div><span style={ls}>Pipe Type</span><select value={se.pipeType} onChange={function(e) { onUpdateEdge(se.id, "pipeType", e.target.value); }} style={sst}>{PTYPES.map(function(t) { return <option key={t} value={t}>{t}</option>; })}</select></div>
            </div>
            <button onClick={function() { onRemoveEdge(se.id); }}
              style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: C.red, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", marginTop: 8 }}>
              Remove Pipe Segment
            </button>
          </>
        )}
      </div>
    );
  }

  // Nothing selected — summary
  var sm = nSum(network);
  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Network Summary</div>
      {cylSize && <div style={{ fontSize: 12, color: C.t2, marginBottom: 8 }}>Cylinder: <strong>{cylSize}</strong></div>}
      <div style={{ fontSize: 12, color: C.t3, marginBottom: 16 }}>Click a node or pipe to select & edit</div>
      <div style={{ fontSize: 12, color: C.t2, lineHeight: 2 }}>
        <div>Pipe Segments: <strong>{sm.totalEdges}</strong></div>
        <div>Nozzles: <strong>{sm.nozzles}</strong>{suggestedNozzles > 0 && <span style={{ color: sm.nozzles === suggestedNozzles ? C.ok : "#B45309" }}> (suggested: {suggestedNozzles})</span>}</div>
        <div>Tees: <strong>{sm.tees}</strong></div>
      </div>
      {Object.keys(sm.pipes).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.navy, marginBottom: 6 }}>Pipe Lengths</div>
          {Object.entries(sm.pipes).map(function(e) {
            return <div key={e[0]} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.t2, padding: "3px 0", borderBottom: "1px solid " + C.border }}><span>ø{e[0]}</span><span><strong>{e[1].length.toFixed(2)}</strong> {uL} ({e[1].count} pcs)</span></div>;
          })}
        </div>
      )}
      <div style={{ marginTop: 20, padding: 12, borderRadius: 8, background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
        <div style={{ fontSize: 11, color: "#1E40AF", lineHeight: 1.6 }}>
          <strong>How to use:</strong><br />
          1. Click on node 1 (hose end) to start<br />
          2. Set pipe length, diameter & type<br />
          3. Click a compass direction to add pipe<br />
          4. Use UP/DOWN for elevation changes<br />
          5. Add nozzles at terminal nodes<br /><br />
          <strong>Navigation:</strong><br />
          • Scroll to zoom in/out<br />
          • Right-click + drag to pan<br />
          • Shift + click + drag to pan<br />
          • Use Rotate button for different angles<br /><br />
          <strong>Tips:</strong><br />
          • Click on a measurement to select & edit that pipe<br />
          • Tees auto-create at branching points<br />
          • New nodes auto-select for quick building<br />
          • Discharge hose from cylinder is pre-set
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───
export default function PipeNetworkDrawer({ enclosureName, enclosureIndex, nozzleType, isMetric, initialData, onSave, onClose, suggestedNozzles, cylSize, valveMM }) {
  var _n = useState(function() { return initialData || initNet(); }), net = _n[0], setNet = _n[1];
  var _s = useState(null), sel = _s[0], setSel = _s[1];
  var _z = useState(1), zoom = _z[0], setZoom = _z[1];
  var _p = useState({ x: 0, y: 0 }), pan = _p[0], setPan = _p[1];
  var _h = useState([]), hist = _h[0], setHist = _h[1];
  var _ip = useState(false), isPan = _ip[0], setIP = _ip[1];
  var _rot = useState(0), rot = _rot[0], setRot = _rot[1];
  var psRef = useRef({ x: 0, y: 0 }), poRef = useRef({ x: 0, y: 0 });

  var pushH = useCallback(function() { setHist(function(h) { return h.slice(-30).concat([JSON.stringify(net)]); }); }, [net]);
  var undo = useCallback(function() { if (hist.length === 0) return; setNet(JSON.parse(hist[hist.length - 1])); setHist(function(h) { return h.slice(0, -1); }); setSel(null); }, [hist]);
  var clear = useCallback(function() { pushH(); setNet(initNet()); setSel(null); setPan({ x: 0, y: 0 }); setZoom(1); }, [pushH]);

  // Pan / Zoom
  var onWhl = useCallback(function(e) { e.preventDefault(); setZoom(function(z) { return Math.max(MNZ, Math.min(MXZ, z + (e.deltaY > 0 ? -.15 : .15))); }); }, []);
  var onPS = useCallback(function(e) { if (e.button === 2 || (e.button === 0 && e.shiftKey)) { e.preventDefault(); setIP(true); psRef.current = { x: e.clientX, y: e.clientY }; poRef.current = { x: pan.x, y: pan.y }; } }, [pan]);
  var onPM = useCallback(function(e) { if (!isPan) return; var b = isoBounds(net, rot); var sc = (b.w / zoom) / (e.currentTarget.clientWidth || 800); setPan({ x: poRef.current.x - (e.clientX - psRef.current.x) * sc, y: poRef.current.y - (e.clientY - psRef.current.y) * sc }); }, [isPan, zoom, net, rot]);
  var onPE = useCallback(function() { setIP(false); }, []);

  // Add pipe — auto-selects the new node
  var addPipe = useCallback(function(fid, dir, len, dia, pt) {
    pushH();
    var newNodeId = null;
    setNet(function(p) {
      var fn = nById(p, fid); if (!fn) return p;
      var dv = DIRS[dir]; if (!dv) return p;
      var nx = fn.x + dv.dx * len * SC, ny = fn.y + dv.dy * len * SC, nz = (fn.z || 0) + dv.dz * len * SC;
      newNodeId = "n" + p.nextLabel;
      var eid = "e" + gid();
      var ee = eFor(p, fid);
      var un = p.nodes.map(function(n) { return (n.id === fid && ee.length >= 2 && n.type === "junction") ? Object.assign({}, n, { type: "tee" }) : n; });
      return {
        nodes: un.concat([{ id: newNodeId, type: "junction", x: nx, y: ny, z: nz, label: String(p.nextLabel) }]),
        edges: p.edges.concat([{ id: eid, from: fid, to: newNodeId, length: len.toFixed(2), diameter: dia, pipeType: pt, direction: dir }]),
        nextLabel: p.nextLabel + 1
      };
    });
    // Auto-select the new node after state update
    setTimeout(function() { if (newNodeId) setSel(newNodeId); }, 10);
  }, [pushH]);

  var addNoz = useCallback(function(nid) { pushH(); setNet(function(p) { return Object.assign({}, p, { nodes: p.nodes.map(function(n) { return n.id === nid ? Object.assign({}, n, { type: "nozzle", nozzleType: (nozzleType || "").indexOf("360") >= 0 ? "360°" : "180°" }) : n; }) }); }); }, [pushH, nozzleType]);

  var updEdge = useCallback(function(eid, key, val) {
    pushH();
    setNet(function(p) {
      var eg = p.edges.find(function(e) { return e.id === eid; }); if (!eg) return p;
      if (key === "length") {
        var fn = nById(p, eg.from), dv = DIRS[eg.direction];
        if (fn && dv) {
          var nl = parseFloat(val) || 1;
          return { nodes: p.nodes.map(function(n) { return n.id === eg.to ? Object.assign({}, n, { x: fn.x + dv.dx * nl * SC, y: fn.y + dv.dy * nl * SC, z: (fn.z || 0) + dv.dz * nl * SC }) : n; }), edges: p.edges.map(function(e) { return e.id === eid ? Object.assign({}, e, { length: val }) : e; }), nextLabel: p.nextLabel };
        }
      }
      var u = {}; u[key] = val;
      return Object.assign({}, p, { edges: p.edges.map(function(e) { return e.id === eid ? Object.assign({}, e, u) : e; }) });
    });
  }, [pushH]);

  var rmNode = useCallback(function(nid) {
    pushH();
    setNet(function(p) {
      var ee = eFor(p, nid), eids = ee.map(function(e) { return e.id; }), cids = ee.map(function(e) { return e.from === nid ? e.to : e.from; });
      var nn = p.nodes.filter(function(n) { return n.id !== nid; }).map(function(n) {
        if (cids.indexOf(n.id) >= 0 && n.type === "tee") {
          var r = p.edges.filter(function(e) { return eids.indexOf(e.id) < 0 && (e.from === n.id || e.to === n.id); });
          if (r.length <= 1) return Object.assign({}, n, { type: "junction" });
        }
        return n;
      });
      return { nodes: nn, edges: p.edges.filter(function(e) { return eids.indexOf(e.id) < 0; }), nextLabel: p.nextLabel };
    });
    setSel(null);
  }, [pushH]);

  var rmEdge = useCallback(function(eid) {
    pushH();
    setNet(function(p) {
      var eg = p.edges.find(function(e) { return e.id === eid; }); if (!eg) return p;
      var te = eFor(p, eg.to), rn = te.length === 1 ? eg.to : null;
      var nn = rn ? p.nodes.filter(function(n) { return n.id !== rn; }) : p.nodes.slice();
      nn = nn.map(function(n) {
        if (n.id === eg.from && n.type === "tee") {
          var r = p.edges.filter(function(e) { return e.id !== eid && (e.from === n.id || e.to === n.id); });
          if (r.length <= 1) return Object.assign({}, n, { type: "junction" });
        }
        return n;
      });
      return { nodes: nn, edges: p.edges.filter(function(e) { return e.id !== eid; }), nextLabel: p.nextLabel };
    });
    setSel(null);
  }, [pushH]);

  // Render data
  var sm = nSum(net);
  var bds = isoBounds(net, rot);
  var vbW = bds.w / zoom, vbH = bds.h / zoom;
  var vbCx = bds.x + bds.w / 2, vbCy = bds.y + bds.h / 2;
  var vb = (vbCx - vbW / 2 + pan.x) + " " + (vbCy - vbH / 2 + pan.y) + " " + vbW + " " + vbH;
  var uL = isMetric ? "m" : "ft";

  // Axis indicator position
  var axX = vbCx - vbW / 2 + pan.x + vbW - 50, axY = vbCy - vbH / 2 + pan.y + 45, al = 22;
  var aN = iso(0, -al, 0, rot), aS = iso(0, al, 0, rot), aE = iso(al, 0, 0, rot), aW = iso(-al, 0, 0, rot);

  // Cylinder position in iso for orientation lines
  var cylNode = nById(net, "cyl");
  var cylIso = cylNode ? iso(cylNode.x, cylNode.y, cylNode.z || 0, rot) : null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(2px)" }} onClick={onClose} />
      <div style={{ position: "relative", marginLeft: "auto", width: "calc(100vw - 60px)", maxWidth: 1300, background: C.white, boxShadow: "-8px 0 40px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", borderRadius: "16px 0 0 16px", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "14px 24px", borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: "linear-gradient(135deg," + C.navy + " 0%," + C.navyLight + " 100%)" }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: C.white, margin: 0 }}>Pipe Network — {enclosureName || "Enclosure " + ((enclosureIndex || 0) + 1)}</h3>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>Isometric Drawing Tool{rot > 0 ? " · Rotated " + rot + "°" : ""}</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={function() { setZoom(function(z) { return Math.min(z + .2, MXZ); }); }} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: C.white, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>+</button>
            <button onClick={function() { setZoom(function(z) { return Math.max(z - .2, MNZ); }); }} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: C.white, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>−</button>
            <span style={{ padding: "5px 6px", fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{Math.round(zoom * 100)}%</span>
            <button onClick={function() { setRot(function(r) { return (r + 90) % 360; }); }} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.15)", color: C.white, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>⟲ Rotate</button>
            <button onClick={function() { setPan({ x: 0, y: 0 }); setZoom(1); setRot(0); }} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: C.white, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Reset</button>
            <button onClick={undo} disabled={hist.length === 0} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: hist.length === 0 ? "rgba(255,255,255,0.3)" : C.white, fontSize: 12, cursor: hist.length === 0 ? "not-allowed" : "pointer", fontFamily: "inherit" }}>Undo</button>
            <button onClick={clear} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.1)", color: C.white, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Clear</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Canvas */}
          <div style={{ flex: 1, padding: 8, overflow: "hidden" }} onClick={function() { if (!isPan) setSel(null); }}>
            <svg width="100%" height="100%" viewBox={vb} style={{ background: "#F8FAFB", borderRadius: 8, cursor: isPan ? "grabbing" : "grab" }}
              onWheel={onWhl} onMouseDown={onPS} onMouseMove={onPM} onMouseUp={onPE} onMouseLeave={onPE}
              onContextMenu={function(e) { e.preventDefault(); }}>

              {/* Orientation lines through cylinder */}
              {cylIso && <OrientationLines cx={cylIso.sx} cy={cylIso.sy} rot={rot} />}

              {/* Axis indicator */}
              <g>
                <line x1={axX} y1={axY} x2={axX + aN.sx} y2={axY + aN.sy} stroke={C.t3} strokeWidth=".8" />
                <text x={axX + aN.sx + 2} y={axY + aN.sy - 3} fontSize="8" fontWeight="700" fill={C.t2}>N</text>
                <line x1={axX} y1={axY} x2={axX + aS.sx} y2={axY + aS.sy} stroke={C.t3} strokeWidth=".8" />
                <text x={axX + aS.sx - 4} y={axY + aS.sy + 8} fontSize="8" fill={C.t3}>S</text>
                <line x1={axX} y1={axY} x2={axX + aE.sx} y2={axY + aE.sy} stroke={C.t3} strokeWidth=".8" />
                <text x={axX + aE.sx + 2} y={axY + aE.sy + 8} fontSize="8" fill={C.t3}>E</text>
                <line x1={axX} y1={axY} x2={axX + aW.sx} y2={axY + aW.sy} stroke={C.t3} strokeWidth=".8" />
                <text x={axX + aW.sx - 10} y={axY + aW.sy - 3} fontSize="8" fill={C.t3}>W</text>
                <line x1={axX} y1={axY} x2={axX} y2={axY - al} stroke={C.t3} strokeWidth=".8" />
                <text x={axX + 3} y={axY - al - 2} fontSize="7" fill={C.t3}>UP</text>
                <line x1={axX} y1={axY} x2={axX} y2={axY + al} stroke={C.t3} strokeWidth=".8" />
                <text x={axX + 3} y={axY + al + 6} fontSize="7" fill={C.t3}>DN</text>
              </g>

              {/* Edges */}
              {net.edges.map(function(eg) {
                var fn = nById(net, eg.from), tn = nById(net, eg.to);
                if (!fn || !tn) return null;
                var fp = iso(fn.x, fn.y, fn.z || 0, rot), tp = iso(tn.x, tn.y, tn.z || 0, rot);
                var sl = sel === eg.id;
                var mx = (fp.sx + tp.sx) / 2, my = (fp.sy + tp.sy) / 2;
                var isHose = eg.edgeType === "hose";
                // Hose = solid blue, not dashed
                var sw = isHose ? 2 : Math.max(1.5, Math.min((parseInt(eg.diameter) || 25) / 10, 3.5));
                var color = isHose ? NC.hose : (sl ? "#F59E0B" : "#1E293B");
                return (
                  <g key={eg.id} onClick={function(ev) { ev.stopPropagation(); setSel(eg.id); }} style={{ cursor: "pointer" }}>
                    <line x1={fp.sx} y1={fp.sy} x2={tp.sx} y2={tp.sy} stroke={color} strokeWidth={sl ? sw + 1.5 : sw} strokeLinecap="round" />
                    {!isHose && (
                      <>
                        <rect x={mx - 22} y={my - 7} width={44} height={14} rx={3} fill="white" stroke={sl ? "#F59E0B" : C.border} strokeWidth=".5" opacity=".92" />
                        <text x={mx} y={my + 3} textAnchor="middle" fontSize="8" fontWeight="600" fill={C.t1} style={{ userSelect: "none" }}>{eg.length}{uL}</text>
                        <text x={mx} y={my + 14} textAnchor="middle" fontSize="7" fill={C.t3} style={{ userSelect: "none" }}>
                          ø{eg.diameter}mm{eg.direction === "UP" || eg.direction === "DOWN" ? " (" + eg.direction + ")" : ""}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}

              {/* Nodes */}
              {net.nodes.map(function(nd) {
                var p = iso(nd.x, nd.y, nd.z || 0, rot), sl = sel === nd.id;
                var clk = function(ev) { ev.stopPropagation(); setSel(nd.id); };
                if (nd.type === "cylinder") return <g key={nd.id} onClick={clk} style={{ cursor: "pointer" }}><CylSVG sx={p.sx} sy={p.sy} sel={sl} cylSize={cylSize} /></g>;
                if (nd.type === "nozzle") return <g key={nd.id} onClick={clk} style={{ cursor: "pointer" }}><NozSVG sx={p.sx} sy={p.sy} sel={sl} nType={nd.nozzleType} label={nd.label} /></g>;
                return <g key={nd.id} onClick={clk} style={{ cursor: "pointer" }}><JncSVG sx={p.sx} sy={p.sy} sel={sl} label={nd.label} type={nd.type} /></g>;
              })}
            </svg>
          </div>

          {/* Sidebar */}
          <div style={{ width: 340, borderLeft: "1px solid " + C.border, overflowY: "auto", flexShrink: 0, background: C.white }}>
            <PropPanel network={net} selectedId={sel} onUpdateEdge={updEdge} onAddPipe={addPipe} onAddNozzle={addNoz} onRemoveNode={rmNode} onRemoveEdge={rmEdge} isMetric={isMetric} nozzleType={nozzleType} suggestedNozzles={suggestedNozzles} cylSize={cylSize} valveMM={valveMM} rot={rot} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 24px", borderTop: "1px solid " + C.border, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, background: C.grayBg }}>
          <div style={{ fontSize: 12, color: C.t3 }}>{sm.totalEdges} pipes · {sm.nozzles} nozzles · {sm.tees} tees</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "10px 24px", borderRadius: 10, border: "1px solid " + C.border, background: C.white, color: C.t1, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button onClick={function() { onSave({ network: net, summary: nSum(net) }); }} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: "linear-gradient(135deg," + C.navy + " 0%," + C.navyLight + " 100%)", color: C.white, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 16px " + C.navy + "30" }}>Save Network</button>
          </div>
        </div>
      </div>
    </div>
  );
}
